import React from "react";

import { CheckboxEmpty, CheckboxFilled } from "./icons";

export type BulkSelectCheckboxProps = {
  checked: boolean;
  onToggle: (e: React.SyntheticEvent) => void;
  className?: string;
  style?: React.CSSProperties;
  /**
   * Stop pointer/mouse down from bubbling — needed when the parent starts a
   * drag (kanban) so a checkbox click doesn't begin dragging the card.
   */
  stopDragPropagation?: boolean;
  /** Icon size in px. Defaults to 18 to match card leading slots. */
  size?: number;
};

/**
 * Shared bulk-select checkbox used in place of a card's leading thumbnail/icon.
 * Keeps the filled/empty icons + white inner fill in one place so AssetCard,
 * WorkOrderCard, TicketCard, and kanban cards don't copy-paste the markup.
 */
const BulkSelectCheckbox = ({
  checked,
  onToggle,
  className = "",
  style,
  stopDragPropagation = false,
  size = 18,
}: BulkSelectCheckboxProps) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onToggle(e);
  };

  return (
    <span
      className={`relative flex items-center justify-center cursor-pointer flex-shrink-0 ${className}`}
      style={style}
      onClick={handleClick}
      onPointerDown={
        stopDragPropagation ? (e) => e.stopPropagation() : undefined
      }
      onMouseDown={
        stopDragPropagation ? (e) => e.stopPropagation() : undefined
      }
    >
      {checked ? (
        <CheckboxFilled width={size} height={size} className="flex-shrink-0" />
      ) : (
        // Empty checkbox has a transparent interior; white inner fill so it
        // reads clearly on hovered (bg-accent) rows.
        <span
          className="relative flex items-center justify-center"
          style={{ width: size, height: size }}
        >
          <span
            className="absolute rounded-[4px] bg-white"
            style={{ inset: 2 }}
          />
          <CheckboxEmpty
            width={size}
            height={size}
            className="relative flex-shrink-0"
          />
        </span>
      )}
    </span>
  );
};

export default BulkSelectCheckbox;
